import React, { useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import {
  Building2,
  Calendar,
  Camera,
  KeyRound,
  Loader2,
  Mail,
  Pencil,
  Save,
  Trash2,
  ShieldCheck,
  User as UserIcon,
  X,
} from "lucide-react";

const inputCls =
  "w-full border border-white/20 bg-white/5 text-white placeholder:text-slate-500 rounded-md px-3 py-2 text-sm transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))] focus:border-[hsl(var(--primary))]";
const labelCls = "text-xs tracking-[0.2em] uppercase font-bold text-slate-500";
const primaryBtn =
  "bg-[hsl(var(--primary))] hover:opacity-90 text-[hsl(var(--primary-foreground))] font-semibold rounded-md px-4 py-2 transition-all duration-150 active:scale-[0.97] flex items-center justify-center gap-2 disabled:opacity-60";
const secondaryBtn =
  "bg-white/10 hover:bg-white/15 text-white font-semibold rounded-md px-4 py-2 transition-all duration-150 active:scale-[0.97] flex items-center justify-center gap-2";

function SectionCard({ title, icon: Icon, children }) {
  return (
    <div className="bg-[#141414] border border-white/10 hover:border-white/20 rounded-lg shadow-sm p-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-black/30">
      <div className="flex items-center gap-3 mb-3 border-b border-white/10 pb-3">
        <div className="h-8 w-8 rounded-md bg-[hsl(var(--primary))]/10 flex items-center justify-center">
          <Icon className="h-5 w-5 text-[hsl(var(--primary))]" />
        </div>
        <h3 className="font-heading text-lg font-bold text-white">{title}</h3>
      </div>
      {children}
    </div>
  );
}

// Shrink the chosen photo to a small square JPEG so it can live on the user record.
function resizeToDataUrl(file, size = 256) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const side = Math.min(img.width, img.height);
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      canvas
        .getContext("2d")
        .drawImage(img, (img.width - side) / 2, (img.height - side) / 2, side, side, 0, 0, size, size);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read this image"));
    };
    img.src = url;
  });
}

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

export default function Profile() {
  const { user, setUser } = useAuth();
  const { branches } = useBranch();
  const isOwner = user?.role === "owner";

  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(user?.name || "");
  const [savingName, setSavingName] = useState(false);

  const [savingAvatar, setSavingAvatar] = useState(false);
  const fileRef = useRef(null);

  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [savingPwd, setSavingPwd] = useState(false);

  const initial = (user?.name || user?.email || "?").charAt(0).toUpperCase();
  const assignedBranch = branches.find((b) => b.id === user?.branch_id);

  const saveName = async () => {
    if (!nameDraft.trim()) return;
    setSavingName(true);
    try {
      const { data } = await api.patch("/auth/me", { name: nameDraft.trim() });
      if (typeof setUser === "function") setUser(data);
      toast.success("Name updated");
      setEditingName(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingName(false);
    }
  };

  const saveAvatar = async (avatar) => {
    setSavingAvatar(true);
    try {
      const { data } = await api.patch("/auth/me", { avatar });
      if (typeof setUser === "function") setUser(data);
      toast.success(avatar ? "Photo updated" : "Photo removed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingAvatar(false);
    }
  };

  const onPickPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    try {
      await saveAvatar(await resizeToDataUrl(file));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const savePassword = async () => {
    if (pwd.next !== pwd.confirm) {
      toast.error("New password and confirmation don't match");
      return;
    }
    setSavingPwd(true);
    try {
      await api.patch("/auth/me", {
        current_password: pwd.current,
        new_password: pwd.next,
      });
      toast.success("Password changed");
      setPwd({ current: "", next: "", confirm: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingPwd(false);
    }
  };

  return (
    <div data-testid="profile-page">
      <div className="mb-8">
        <h1 className="font-heading text-4xl tracking-tight font-black text-white">
          My Profile
        </h1>
        <p className="text-sm text-slate-500 mt-2">
          Your account details and security.
        </p>
      </div>

      <div className="flex flex-col gap-8 max-w-4xl">
        {/* Identity card */}
        <div className="profile-hero-card bg-[#141414] border border-white/10 rounded-lg shadow-sm p-6 relative overflow-hidden">
          <style>{`
            .profile-hero-card { animation: profile-rise-in 420ms cubic-bezier(0.22,1,0.36,1) both; }
            .profile-avatar-glow {
              position: relative;
              width: 76px;
              height: 76px;
              border-radius: 20px;
              display: flex;
              align-items: center;
              justify-content: center;
              background: linear-gradient(135deg, hsl(var(--primary)), color-mix(in srgb, hsl(var(--primary)) 55%, black));
              box-shadow: 0 14px 30px -8px hsl(var(--primary) / 0.55);
              flex-shrink: 0;
            }
            .profile-avatar-glow::before {
              content: '';
              position: absolute;
              inset: -14px;
              border-radius: 26px;
              background: radial-gradient(circle, hsl(var(--primary) / 0.35), transparent 70%);
              animation: profile-glow-pulse 3.2s ease-in-out infinite;
              z-index: -1;
            }
            @keyframes profile-glow-pulse {
              0%, 100% { opacity: 0.55; transform: scale(1); }
              50% { opacity: 0.95; transform: scale(1.1); }
            }
            @keyframes profile-rise-in {
              from { opacity: 0; transform: translateY(10px); }
              to { opacity: 1; transform: translateY(0); }
            }
            @media (prefers-reduced-motion: reduce) {
              .profile-hero-card, .profile-avatar-glow::before { animation: none !important; }
            }
          `}</style>

          <div className="flex items-start gap-5">
            <div className="flex flex-col items-center gap-2 shrink-0">
              <div className="profile-avatar-glow group">
                {user?.avatar ? (
                  <img src={user.avatar} alt="" className="h-full w-full object-cover rounded-[20px]" />
                ) : (
                  <span className="text-3xl font-heading font-black text-white">{initial}</span>
                )}
                <button
                  type="button"
                  data-testid="profile-photo-button"
                  title="Change photo"
                  onClick={() => fileRef.current?.click()}
                  disabled={savingAvatar}
                  className="absolute -bottom-1.5 -right-1.5 h-8 w-8 rounded-full bg-white text-black flex items-center justify-center shadow-lg hover:scale-105 active:scale-95 transition-all duration-150 disabled:opacity-60"
                >
                  {savingAvatar ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
                </button>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickPhoto} />
              </div>
              {user?.avatar && (
                <button
                  type="button"
                  data-testid="profile-remove-photo-button"
                  onClick={() => saveAvatar("")}
                  disabled={savingAvatar}
                  className="flex items-center gap-1 text-[11px] text-slate-500 hover:text-red-400 transition-colors duration-150"
                >
                  <Trash2 className="h-3 w-3" /> Remove
                </button>
              )}
            </div>

            <div className="flex-1 min-w-0">
              {editingName ? (
                <div className="flex items-center gap-2 mb-1">
                  <input
                    data-testid="profile-name-input"
                    autoFocus
                    className={`${inputCls} max-w-xs`}
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && saveName()}
                  />
                  <button
                    data-testid="profile-save-name-button"
                    className={primaryBtn}
                    onClick={saveName}
                    disabled={savingName}
                  >
                    {savingName ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  </button>
                  <button
                    className={secondaryBtn}
                    onClick={() => {
                      setEditingName(false);
                      setNameDraft(user?.name || "");
                    }}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2 mb-1">
                  <h2 className="font-heading text-2xl font-bold text-white truncate">
                    {user?.name || "Unnamed"}
                  </h2>
                  <button
                    data-testid="profile-edit-name-button"
                    title="Edit name"
                    onClick={() => setEditingName(true)}
                    className="p-1.5 rounded-md text-slate-500 hover:text-white hover:bg-white/10 transition-all duration-150"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}

              <span
                className={`inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase font-bold rounded px-2 py-0.5 ${
                  isOwner
                    ? "text-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10"
                    : "text-slate-300 bg-white/10"
                }`}
              >
                <ShieldCheck className="h-3 w-3" />
                {isOwner ? "Owner" : "Staff"}
              </span>

              <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="flex items-center gap-2 text-sm text-slate-300">
                  <Mail className="h-4 w-4 text-slate-500 shrink-0" />
                  <span className="truncate">{user?.email}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-300">
                  <Building2 className="h-4 w-4 text-slate-500 shrink-0" />
                  <span className="truncate">
                    {isOwner ? `${branches.length} branch${branches.length === 1 ? "" : "es"}` : assignedBranch?.name || "—"}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-300">
                  <Calendar className="h-4 w-4 text-slate-500 shrink-0" />
                  <span className="truncate">Member since {formatDate(user?.created_at)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Change password */}
        <SectionCard title="Change Password" icon={KeyRound}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={labelCls}>Current Password</label>
              <input
                data-testid="profile-current-password-input"
                type="password"
                className={`mt-1.5 ${inputCls}`}
                value={pwd.current}
                onChange={(e) => setPwd({ ...pwd, current: e.target.value })}
              />
            </div>
            <div>
              <label className={labelCls}>New Password</label>
              <input
                data-testid="profile-new-password-input"
                type="password"
                className={`mt-1.5 ${inputCls}`}
                value={pwd.next}
                onChange={(e) => setPwd({ ...pwd, next: e.target.value })}
              />
            </div>
            <div>
              <label className={labelCls}>Confirm New Password</label>
              <input
                data-testid="profile-confirm-password-input"
                type="password"
                className={`mt-1.5 ${inputCls}`}
                value={pwd.confirm}
                onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
              />
            </div>
          </div>
          <div className="mt-6">
            <button
              data-testid="profile-save-password-button"
              className={primaryBtn}
              onClick={savePassword}
              disabled={savingPwd || !pwd.current || !pwd.next || !pwd.confirm}
            >
              {savingPwd ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserIcon className="h-4 w-4" />}
              Update Password
            </button>
          </div>
        </SectionCard>
      </div>
    </div>
  );
}
